// Country Mapping Data
export const COUNTRY_MAPPING: Record<string, { name: string; emoji: string }> =
  {
    // Standard countries
    ARE: { name: 'United Arab Emirates', emoji: '🇦🇪' },
    ARG: { name: 'Argentina', emoji: '🇦🇷' },
    AUS: { name: 'Australia', emoji: '🇦🇺' },
    AUT: { name: 'Austria', emoji: '🇦🇹' },
    BEL: { name: 'Belgium', emoji: '🇧🇪' },
    BGR: { name: 'Bulgaria', emoji: '🇧🇬' },
    CAN: { name: 'Canada', emoji: '🇨🇦' },
    CHE: { name: 'Switzerland', emoji: '🇨🇭' },
    CHN: { name: 'China', emoji: '🇨🇳' },
    CZE: { name: 'Czech Republic', emoji: '🇨🇿' },
    DEU: { name: 'Germany', emoji: '🇩🇪' },
    DNK: { name: 'Denmark', emoji: '🇩🇰' },
    ESP: { name: 'Spain', emoji: '🇪🇸' },
    FIN: { name: 'Finland', emoji: '🇫🇮' },
    FRA: { name: 'France', emoji: '🇫🇷' },
    GBR: { name: 'United Kingdom', emoji: '🇬🇧' },
    GRC: { name: 'Greece', emoji: '🇬🇷' },
    HKG: { name: 'Hong Kong', emoji: '🇭🇰' },
    ITA: { name: 'Italy', emoji: '🇮🇹' },
    JPN: { name: 'Japan', emoji: '🇯🇵' },
    MCO: { name: 'Monaco', emoji: '🇲🇨' },
    NLD: { name: 'Netherlands', emoji: '🇳🇱' },
    NOR: { name: 'Norway', emoji: '🇳🇴' },
    POL: { name: 'Poland', emoji: '🇵🇱' },
    PRT: { name: 'Portugal', emoji: '🇵🇹' },
    SWE: { name: 'Sweden', emoji: '🇸🇪' },
    USA: { name: 'United States', emoji: '🇺🇸' },
    ZAF: { name: 'South Africa', emoji: '🇿🇦' },
    IRL: { name: 'Ireland', emoji: '🇮🇪' },
    LUX: { name: 'Luxembourg', emoji: '🇱🇺' },
    MEX: { name: 'Mexico', emoji: '🇲🇽' },
    BRA: { name: 'Brazil', emoji: '🇧🇷' },
    NZL: { name: 'New Zealand', emoji: '🇳🇿' },
    KWT: { name: 'Kuwait', emoji: '🇰🇼' },
    LIE: { name: 'Liechtenstein', emoji: '🇱🇮' },
    HRV: { name: 'Croatia', emoji: '🇭🇷' },

    // Add fallback for "not specified"
    'NOT SPECIFIED': { name: 'Not Specified', emoji: '❓' },
  };

// Only the groups we want visible in the sidebar, in this exact order.
// 'Retromods' is intentionally removed — those listings are classified
// as base variant (e.g. Carrera 2) + build field, and should not surface
// as a separate variant group.
export const VARIANT_GROUP_ORDER = [
  'Carreras',
  'Turbos',
  'Competition',
];

// Helper function to safely extract count from facet data
export const extractCount = (data: any): number => {
  if (typeof data === 'number') {
    return data;
  }
  if (typeof data === 'object' && data !== null) {
    if ('count' in data && typeof data.count === 'number') {
      return data.count;
    }
    if ('value' in data && typeof data.value === 'number') {
      return data.value;
    }
    const values = Object.values(data);
    const numValue = values.find((v) => typeof v === 'number');
    return (numValue as number) || 0;
  }
  return 0;
};

// Helper function to safely extract display name - NO MODIFICATIONS
export const extractDisplayName = (data: any, fallback: string): string => {
  if (typeof data === 'string') {
    return data;
  }
  if (typeof data === 'object' && data !== null) {
    if ('value' in data) {
      return data.value;
    }
    const keys = Object.keys(data);
    if (keys.length > 0) {
      return keys[0];
    }
  }
  return fallback;
};

// Maps a listing_variant_sub_group value to its display group in the sidebar.
// Only known variant names route to Carreras/Turbos/Competition.
// Anything unrecognised returns 'Other' which is excluded from VARIANT_GROUP_ORDER
// spurious groups like "Retromods".
export const mapSubGroupToTopGroup = (subGroup: string): string => {
  if (!subGroup || typeof subGroup !== 'string') {
    return 'Other';
  }

  const subGroupLower = subGroup.toLowerCase();

  // Carreras group
  if (
    subGroupLower.includes('carrera') ||
    subGroupLower.includes('rs america') ||
    subGroupLower.includes('speedster') ||
    subGroupLower.includes('america roadster')
  ) {
    return 'Carreras';
  }

  // Turbos group
  if (subGroupLower.includes('turbo')) {
    return 'Turbos';
  }

  // Competition group
  if (
    subGroupLower.includes('cup') ||
    subGroupLower.includes('gt1') ||
    subGroupLower.includes('gt2')
  ) {
    return 'Competition';
  }

  // Everything else (including any stray build values like "Singer", "RUF",
  // "custom", or null-ish strings) falls to 'Other' and is hidden from sidebar.
  return 'Other';
};

export const formatWithDot = (value: string | number): string => {
  const num =
    typeof value === 'string' ? parseInt(value.replace(/\D/g, '')) || 0 : value;
  return num.toLocaleString('de-DE', {
    maximumFractionDigits: 0,
    useGrouping: true,
  });
};

export const roundToNearest10k = (value: number): number =>
  Math.round(value / 10000) * 10000;

export {};